import { OFFLINE_STORES, getOfflineStorageDriver, atomicPutBatch } from "./offlineStorageAdapter.js";
import { cloneVisitaSnapshot, createLocalFirstPayload, withVisitaLocalLock, VISITA_DRAFT_ACKNOWLEDGED, VISITA_STRUCTURE_ACKNOWLEDGED } from './visitaLocalFirst.js';
import { sanitizePayloadWithActiveReconciliations } from "./visitaInstanceReconciler.js";
import { getVisitaOfflineOperationsByVisita } from './visitaOfflineOperationQueue.js';
import { getOfflineImageEntriesByVisita } from './visitaOfflineMediaQueue.js';
import { compactGroupSnapshot, compactMediaMetadata } from './visitaGroupRemoval.js';
import { acknowledgeVisitaContext } from './visitaOfflineContext.js';
import { conflictSnapshotContent, isResolvedConflict } from './offlineConflictIdentity.js';
import {verifiedLegacyRecoveryCoverage} from './legacyVisitRecovery.js';
import {legacySourceFingerprint, unpackLegacyQueue} from './legacyRecoverySources.js';

const LEGACY_STORAGE_KEY = "react_visitas_offline_queue_v1";
const LEGACY_MIGRATION_KEY = "react_visitas_offline_queue_v1_migrated";
const FALLBACK_STORAGE_KEY = "react_visitas_offline_queue_v2_fallback";
const QUEUE_CHANGED_EVENT = "visita-offline-queue-changed";
const DB_NAME = "coordinacion_visitas_offline";
const DB_VERSION = 1;
const STORE_NAME = "visita_offline_entries";

let dbPromise = null;
let migrationPromise = null;

function toNumber(value) {
    const num = Number(value);
    return Number.isFinite(num) ? num : null;
}

function buildEntryKey(userId, visitaId) {
    return `${toNumber(userId) || "anon"}:${toNumber(visitaId) || "0"}`;
}

function emitQueueChanged() {
    if (typeof window === "undefined") {
        return;
    }

    window.dispatchEvent(new CustomEvent(QUEUE_CHANGED_EVENT));
}

export function generateRevisionUuid() {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
        return crypto.randomUUID();
    }
    return `rev_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

function normalizeEntry(entry) {
    if (!entry || typeof entry !== "object") {
        return null;
    }

    const key =
        typeof entry.key === "string" && entry.key.trim().length > 0
            ? entry.key
            : buildEntryKey(entry.userId, entry.visitaId);

    const revisionId =
        typeof entry.revisionId === "string" && entry.revisionId.trim().length > 0
            ? entry.revisionId.trim()
            : generateRevisionUuid();

    return {
        key,
        userId: toNumber(entry.userId),
        visitaId: toNumber(entry.visitaId),
        revisionId,
        payload: entry.payload,
        updatedAt:
            typeof entry.updatedAt === "string"
                ? entry.updatedAt
                : new Date().toISOString(),
    };
}

function readLegacyStore() {
    if (typeof window === "undefined") {
        return [];
    }

    try {
        const raw = window.localStorage.getItem(LEGACY_STORAGE_KEY);
        const parsed = raw ? JSON.parse(raw) : null;
        const entries = parsed?.entries;

        if (!entries || typeof entries !== "object") {
            return [];
        }

        return Object.values(entries)
            .map((entry) => normalizeEntry(entry))
            .filter(Boolean);
    } catch {
        return [];
    }
}

function readFallbackStore() {
    if (typeof window === "undefined") {
        return [];
    }

    try {
        const raw = window.localStorage.getItem(FALLBACK_STORAGE_KEY);
        const parsed = raw ? JSON.parse(raw) : null;
        const entries = Array.isArray(parsed?.entries) ? parsed.entries : [];

        return entries.map((entry) => normalizeEntry(entry)).filter(Boolean);
    } catch {
        return [];
    }
}

function writeFallbackStore(entries) {
    if (typeof window === "undefined") {
        return;
    }

    try {
        window.localStorage.setItem(
            FALLBACK_STORAGE_KEY,
            JSON.stringify({ entries }),
        );
    } catch {
        // Ignorar errores de cuota para no romper la UI.
    }
}

async function recoveredFallback(driver, entryKey, revisionId = null) {
    const text = globalThis.window?.localStorage?.getItem(FALLBACK_STORAGE_KEY);
    if (!text) return false;
    const row = unpackLegacyQueue(JSON.parse(text)).find(item => item.key === entryKey && (!revisionId || item.revisionId === revisionId));
    if (!row) return false;
    return (await verifiedLegacyRecoveryCoverage({driver})).has(await legacySourceFingerprint(FALLBACK_STORAGE_KEY, row));
}

async function removeFallbackEntry(entryKey, expectedRevisionId = null) {
    try {if (await recoveredFallback(getOfflineStorageDriver(), entryKey, expectedRevisionId)) return;}
    catch {return;} // Una lectura desconocida nunca autoriza retirar el original.
    const entries = readFallbackStore().filter((entry) => entry.key !== entryKey
        || (expectedRevisionId && entry.revisionId !== expectedRevisionId));
    writeFallbackStore(entries);
}

async function recoverEmergencyEntry(driver, key, current) {
    if (await recoveredFallback(driver, key)) return current;
    const emergency = readFallbackStore().find((entry) => entry.key === key);
    if (!emergency) return current;
    if (!current || Date.parse(emergency.updatedAt) > Date.parse(current.updatedAt)) {
        await driver.put(OFFLINE_STORES.ENTRIES, emergency);
        removeFallbackEntry(key, emergency.revisionId);
        return emergency;
    }
    removeFallbackEntry(key, emergency.revisionId);
    return current;
}

function setLegacyMigrated() {
    if (typeof window === "undefined") {
        return;
    }

    try {
        window.localStorage.setItem(LEGACY_MIGRATION_KEY, "1");
    } catch {
        // Ignorar errores de quota.
    }
}

function isLegacyMigrated() {
    if (typeof window === "undefined") {
        return true;
    }

    try {
        return window.localStorage.getItem(LEGACY_MIGRATION_KEY) === "1";
    } catch {
        return true;
    }
}

function isIndexedDbAvailable() {
    return typeof window !== "undefined" && typeof window.indexedDB !== "undefined";
}

function openDb() {
    if (dbPromise) {
        return dbPromise;
    }

    if (!isIndexedDbAvailable()) {
        dbPromise = Promise.resolve(null);
        return dbPromise;
    }

    dbPromise = new Promise((resolve) => {
        try {
            const request = window.indexedDB.open(DB_NAME, DB_VERSION);

            request.onupgradeneeded = () => {
                const db = request.result;

                if (!db.objectStoreNames.contains(STORE_NAME)) {
                    const store = db.createObjectStore(STORE_NAME, {
                        keyPath: "key",
                    });
                    store.createIndex("by_user", "userId", { unique: false });
                    store.createIndex("by_updated_at", "updatedAt", {
                        unique: false,
                    });
                }
            };

            request.onsuccess = () => resolve(request.result);
            request.onerror = () => resolve(null);
        } catch {
            resolve(null);
        }
    });

    return dbPromise;
}

function withStore(db, mode, operation) {
    return new Promise((resolve, reject) => {
        try {
            const tx = db.transaction(STORE_NAME, mode);
            const store = tx.objectStore(STORE_NAME);
            operation(store, resolve, reject);
        } catch (error) {
            reject(error);
        }
    });
}

async function migrateLegacyIfNeeded() {
    if (migrationPromise) {
        return migrationPromise;
    }

    migrationPromise = (async () => {
        const db = await openDb();
        if (!db) {
            return;
        }

        if (isLegacyMigrated()) {
            return;
        }

        const legacyEntries = readLegacyStore();
        if (legacyEntries.length === 0) {
            setLegacyMigrated();
            return;
        }

        await Promise.all(
            legacyEntries.map((entry) =>
                withStore(db, "readwrite", (store, resolve, reject) => {
                    const request = store.put(entry);
                    request.onsuccess = () => resolve();
                    request.onerror = () => reject(request.error);
                }),
            ),
        );

        setLegacyMigrated();
    })();

    return migrationPromise;
}

async function migrateFallbackToDb(db) {
    const fallbackEntries = readFallbackStore();
    if (fallbackEntries.length === 0) {
        return;
    }

    await Promise.all(
        fallbackEntries.map((entry) =>
            withStore(db, "readwrite", (store, resolve, reject) => {
                const request = store.put(entry);
                request.onsuccess = () => {
                    removeFallbackEntry(entry.key);
                    resolve();
                };
                request.onerror = () => reject(request.error);
            }),
        ),
    );
}

async function ensureOfflineStoreReady() {
    const db = await openDb();
    if (!db) {
        return null;
    }

    await migrateLegacyIfNeeded();
    await migrateFallbackToDb(db);
    return db;
}

function sortEntriesByUpdatedAt(entries) {
    return [...entries].sort((a, b) => {
        const tsA = Date.parse(a?.updatedAt || "") || 0;
        const tsB = Date.parse(b?.updatedAt || "") || 0;
        return tsA - tsB;
    });
}

export function syncSaveVisitaOfflineEntry({ userId, visitaId, payload, revisionId = null }) {
    try {
        const key = buildEntryKey(userId, visitaId);
        const resolvedRevisionId = revisionId || generateRevisionUuid();
        const sanitizedPayload = payload?._local_first ? payload : sanitizePayloadWithActiveReconciliations(visitaId, payload);
        const entry = {
            key,
            userId: toNumber(userId),
            visitaId: toNumber(visitaId),
            revisionId: resolvedRevisionId,
            payload: sanitizedPayload,
            updatedAt: new Date().toISOString(),
        };

        const current = readFallbackStore().filter((item) => item.key !== key);
        current.push(entry);
        writeFallbackStore(current);
        // We don't emit queue changed event here as this is just a silent fallback for pagehide
    } catch {
        // Ignorar si falla localStorage quota
    }
}

export async function upsertVisitaOfflineEntry({ userId, visitaId, payload, revisionId = null }) {
    const key = buildEntryKey(userId, visitaId);
    const resolvedRevisionId = revisionId || generateRevisionUuid();
    const sanitizedPayload = payload?._local_first ? payload : sanitizePayloadWithActiveReconciliations(visitaId, payload);
    const entry = {
        key,
        userId: toNumber(userId),
        visitaId: toNumber(visitaId),
        revisionId: resolvedRevisionId,
        payload: sanitizedPayload,
        updatedAt: new Date().toISOString(),
    };

    const driver = getOfflineStorageDriver();
    try {
        await driver.put(OFFLINE_STORES.ENTRIES, entry);
        const emergency = readFallbackStore().find((item) => item.key === key);
        if (emergency && Date.parse(emergency.updatedAt) <= Date.parse(entry.updatedAt)) removeFallbackEntry(key, emergency.revisionId);
        emitQueueChanged();
        return entry;
    } catch (error) {
        if (driver.strict || driver.effectiveEngine === 'sqlite') throw error;
        // Fallback a lógica interna
    }

    const db = await ensureOfflineStoreReady();
    if (!db) {
        const current = readFallbackStore().filter((item) => item.key !== key);
        current.push(entry);
        writeFallbackStore(current);
        emitQueueChanged();
        return entry;
    }

    await withStore(db, "readwrite", (store, resolve, reject) => {
        const request = store.put(entry);
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
    });

    emitQueueChanged();
    return entry;
}

/**
 * Guarda atómicamente la adición de un activo de catálogo junto con el borrador actualizado.
 * Garantiza que la operación outbox (add_group) y el borrador de visita (payload con dynamic_group_items)
 * se consoliden en una única transacción transaccional indivisible.
 *
 * @param {object} params
 * @param {number|string} params.userId
 * @param {number|string} params.visitaId
 * @param {object} params.operation - Datos de la operación outbox add_group
 * @param {object} params.draftPayload - Payload completo actualizado de la visita
 * @param {string} [params.revisionId] - Identificador de revisión opcional
 * @returns {Promise<{entry: object, operation: object}>}
 */
export async function saveOfflineCatalogAssetAtomic({
    userId,
    visitaId,
    operation,
    draftPayload,
    revisionId = null,
    baseSnapshot = null,
}) {
    return saveVisitaStructureAtomic({ userId, visitaId, operations: [operation], draftPayload, revisionId, baseSnapshot });
}

export async function saveVisitaStructureAtomic({ userId, visitaId, operations, draftPayload, revisionId = null, baseSnapshot = null }) {
    return withVisitaLocalLock(userId, visitaId, async () => {
        const existing = await getVisitaOfflineEntry({ userId, visitaId });
        return persistVisitaStructure({ userId, visitaId, operations, draftPayload: baseSnapshot
            ? createLocalFirstPayload(draftPayload, baseSnapshot, existing?.payload) : draftPayload, revisionId });
    });
}

async function persistVisitaStructure({ userId, visitaId, operations, draftPayload, revisionId, extraItems = [] }) {
    if (!visitaId || !operations?.length || operations.some((op) => !op) || !draftPayload) {
        throw new Error("Parámetros insuficientes para guardado atómico de activo de catálogo.");
    }

    const key = buildEntryKey(userId, visitaId);
    const resolvedRevisionId = revisionId || generateRevisionUuid();
    const priorOperations = await getVisitaOfflineOperationsByVisita(visitaId, userId);
    const startedAt = Math.max(Date.now(), ...priorOperations.map((op) => (Date.parse(op.createdAt) || 0) + 1));
    const nowIso = new Date(startedAt).toISOString();

    const entry = {
        key,
        userId: toNumber(userId),
        visitaId: toNumber(visitaId),
        revisionId: resolvedRevisionId,
        payload: draftPayload,
        updatedAt: nowIso,
    };

    const effectiveOperations = operations.map((operation, index) => {
        const opUuid = operation.operationUuid || operation.operation_uuid || generateRevisionUuid();
        return {
            id: operation.id || `${toNumber(userId) || "anon"}-${toNumber(visitaId) || "0"}-${operation.type || "add_group"}-${opUuid}`,
            operation_uuid: opUuid,
            userId: toNumber(userId),
            visitaId: toNumber(visitaId),
            type: String(operation.type || "add_group"),
            payload: operation.payload || {},
            revisionId: resolvedRevisionId,
            dependencies: Array.isArray(operation.dependencies) ? operation.dependencies : [],
            status: "pending",
            retryCount: 0,
            lastError: null,
            createdAt: new Date(Date.parse(nowIso) + index).toISOString(),
            updatedAt: nowIso,
        };
    });

    const items = [
        { storeName: OFFLINE_STORES.ENTRIES, value: entry },
        ...effectiveOperations.map((value) => ({ storeName: OFFLINE_STORES.OPERATIONS, value })),
        ...extraItems,
    ];

    await atomicPutBatch(items);

    emitQueueChanged();
    if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent("visita-offline-operation-queue-changed"));
        if (extraItems.length) window.dispatchEvent(new CustomEvent('visita-offline-media-queue-changed'));
    }

    return { entry, operation: effectiveOperations[0], operations: effectiveOperations };
}

export async function saveLocalFirstVisita({ userId, visitaId, snapshot, baseSnapshot, preparedBase = null }) {
    return withVisitaLocalLock(userId, visitaId, async () => {
        const existing = await getVisitaOfflineEntry({ userId, visitaId });
        const payload = createLocalFirstPayload(typeof snapshot === 'function' ? snapshot() : snapshot, baseSnapshot, existing?.payload);
        const prepared = existing?.payload?._merge || preparedBase || (payload.offline_merge_pilot === true ? await (await import('./visitaMergeSync.js')).getMergeBase(userId,visitaId) : null);
        if(prepared)payload._merge=prepared;
        if(prepared&&existing&&JSON.stringify(existing.payload)===JSON.stringify(payload))return existing;
        return upsertVisitaOfflineEntry({ userId, visitaId, payload });
    });
}

export async function saveVisitaRemovalAtomic({ userId, visitaId, snapshot, baseSnapshot, groupIdx, indices, count, baseMultiplier }) {
    return withVisitaLocalLock(userId, visitaId, async () => {
        const existing = await getVisitaOfflineEntry({ userId, visitaId });
        const before = cloneVisitaSnapshot(typeof snapshot === 'function' ? snapshot() : snapshot);
        const compactOptions = { groupIdx, indices, count, baseMultiplier };
        const after = compactGroupSnapshot(before, compactOptions);
        const base = existing?.payload?._local_first?.base || baseSnapshot;
        after._local_first = { base: compactGroupSnapshot(cloneVisitaSnapshot(base), compactOptions) };
        const uuid = generateRevisionUuid();
        const media = await getOfflineImageEntriesByVisita(visitaId, userId);
        const extraItems = media.filter((item) => item.metadata?.instanceRef).map((item) => ({
            storeName: OFFLINE_STORES.MEDIA,
            value: { ...item, metadata: compactMediaMetadata(item.metadata, groupIdx, indices, count) },
        }));
        return persistVisitaStructure({ userId, visitaId, draftPayload: after, extraItems,
            operations: [{ type: 'remove_group', operationUuid: uuid, payload: {
                group_idx: String(groupIdx), instance_indices: [...indices].sort((a, b) => b - a),
                expected_count: count, client_uuid: uuid, local_first: true,
                responses: before.responses, custom_subgroup_names: before.custom_subgroup_names,
                instance_states: before.instance_states,
            } }],
        });
    });
}

export async function acknowledgeVisitaStructure({ userId, visitaId, instanceKey, serverVisita, operationId, operationType = 'add_group', groupIdx = null }) {
    if (!serverVisita || !instanceKey) throw new Error('El servidor no confirmó la instancia creada.');
    return withVisitaLocalLock(userId, visitaId, async () => {
        const entry = await getVisitaOfflineEntry({ userId, visitaId });
        const operations = await getVisitaOfflineOperationsByVisita(visitaId, userId);
        const remaining = operations.filter((op) => op.id !== operationId && op.status !== 'applied');
        // El borrador ya representa todas las restas locales. Un ACK intermedio no puede
        // reintroducir instancias que otra operación pendiente quitó o compactó.
        if (remaining.some((op) => op.type === 'remove_group')
            || (operationType === 'remove_group' && remaining.some((op) => op.type === 'add_group'))) return;
        const dynamicItems = JSON.parse(JSON.stringify(serverVisita.dynamic_group_items || []));
        const instanceOwners = serverVisita.instance_owners ? {...serverVisita.instance_owners} : null;
        for (const op of operations) {
            if (op.id === operationId || op.type !== 'add_group' || op.status === 'applied') continue;
            if (instanceOwners && op.userId === userId && op.payload?.local_instance_key) instanceOwners[op.payload.local_instance_key] = userId;
            const groupKey = String(op.payload?.group_idx);
            const item = dynamicItems.find((value) => String(value.group_parent) === groupKey);
            if (item) item.additional_count = Number(item.additional_count || 0) + 1;
            else dynamicItems.push({ group_parent: groupKey, additional_count: 1 });
        }
        if (entry?.payload) {
            const payload = { ...entry.payload,
                dynamic_group_items: dynamicItems,
                responses: { ...serverVisita.responses, ...entry.payload.responses },
                custom_subgroup_names: { ...serverVisita.custom_subgroup_names, ...entry.payload.custom_subgroup_names },
            };
            if (instanceOwners) payload.instance_owners = instanceOwners;
            if (operationType === 'remove_group') payload.instance_states = { ...serverVisita.instance_states, ...entry.payload.instance_states };
            if (payload._local_first) {
                const base = cloneVisitaSnapshot(payload._local_first.base);
                for (const name of ['responses', 'custom_subgroup_names']) {
                    for (const [key, value] of Object.entries(serverVisita[name] || {})) {
                        if ((operationType === 'remove_group' && (key.startsWith(`${groupIdx}_`) || key.startsWith(`_activo_id_${groupIdx}_`) || key.startsWith(`_gps_${groupIdx}_`)))
                            || key === instanceKey || key.startsWith(`${instanceKey}_`)
                            || key === `_activo_id_${instanceKey}` || key === `_gps_${instanceKey}`) base[name][key] = value;
                    }
                }
                payload._local_first = { base };
            }
            await upsertVisitaOfflineEntry({ userId, visitaId, payload });
        }
        if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent(VISITA_STRUCTURE_ACKNOWLEDGED, {
            detail: { visitaId, instanceKey, serverVisita, dynamicItems, ...(instanceOwners ? {instanceOwners} : {}) },
        }));
    });
}

// El ACK de A solo reconoce A; si ya existe B, se recalcula su delta sin borrarlo.
export async function acknowledgeVisitaDraft(entry, response, { confirmedNoChanges = false } = {}) {
    const data = response?.data || response || {};
    if (!confirmedNoChanges && (data.applied_revision_uuid !== entry.revisionId || data.ignored === true
        || data.conflict === true || !(data.status === 'applied' || (!data.status && data.success === true)))) return false;

    // El ACK no puede retirar el único ejemplar de las respuestas rechazadas por lápidas.
    // Primero conservar el conflicto de la revisión efectivamente enviada; si falla, queda el borrador.
    const conflict = extractDiscardedConflictData(entry.payload, data.discarded_instances || [], {
        userId: entry.userId, visitaId: entry.visitaId, revisionId: entry.revisionId,
    });
    if (conflict) await saveVisitaConflictData(entry.visitaId, conflict, { userId: entry.userId });
    return withVisitaLocalLock(entry.userId, entry.visitaId, async () => {
        await acknowledgeVisitaContext(entry,data);
        const current = await getVisitaOfflineEntry({ userId: entry.userId, visitaId: entry.visitaId });
        const pendingOperations = await getVisitaOfflineOperationsByVisita(entry.visitaId, entry.userId);
        const preserveStructure = pendingOperations.some((op) => op.type === 'remove_group' && op.status !== 'applied');
        if (current?.revisionId === entry.revisionId) {
            await removeVisitaOfflineEntry({ userId: entry.userId, visitaId: entry.visitaId, expectedRevisionId: entry.revisionId });
        } else if (current?.payload?._local_first) {
            if (!preserveStructure) {
                await upsertVisitaOfflineEntry({ userId: entry.userId, visitaId: entry.visitaId,
                    payload: { ...current.payload, _local_first: { base: cloneVisitaSnapshot(entry.payload) } } });
            }
        }
        if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent(VISITA_DRAFT_ACKNOWLEDGED, {
                detail: { visitaId: entry.visitaId, payload: entry.payload, response: data, preserveStructure },
            }));
        }
        return true;
    });
}

export async function getVisitaOfflineEntry({ userId, visitaId }) {
    if (!userId || !visitaId) {
        return null;
    }
    const key = buildEntryKey(userId, visitaId);

    const driver = getOfflineStorageDriver();
    try {
        const item = await recoverEmergencyEntry(driver, key, await driver.get(OFFLINE_STORES.ENTRIES, key));
        if (item || driver.strict || driver.effectiveEngine === 'sqlite') {
            // Una consulta SQLite vacía confirma que no hay borrador pendiente.
            return item || null;
        }
    } catch (error) {
        if (driver.strict || driver.effectiveEngine === 'sqlite') throw error;
        // Fallback a almacenamiento local previo
    }

    const db = await ensureOfflineStoreReady();
    if (!db) {
        const entries = readFallbackStore();
        return entries.find((entry) => entry.key === key) || null;
    }

    return withStore(db, "readonly", (store, resolve, reject) => {
        const request = store.get(key);
        request.onsuccess = () => resolve(request.result || null);
        request.onerror = () => reject(request.error);
    });
}

export async function removeVisitaOfflineEntry({ userId, visitaId, expectedRevisionId = null, force = false }) {
    const key = buildEntryKey(userId, visitaId);

    const driver = getOfflineStorageDriver();
    try {
        if (typeof driver.compareAndDelete === 'function') {
            const deleted = await driver.compareAndDelete(OFFLINE_STORES.ENTRIES, key, (current) => {
                if (!current) {
                    return false;
                }
                if (!force && expectedRevisionId && current.revisionId && current.revisionId !== expectedRevisionId) {
                    return false;
                }
                return true;
            });
            if (deleted) {
                emitQueueChanged();
                return true;
            }
            if (driver?.strict || driver?.effectiveEngine === 'sqlite' || (typeof Capacitor !== 'undefined' && Capacitor.isNativePlatform?.())) {
                return false;
            }
        }
    } catch (driverError) {
        if (driver?.strict || driver?.effectiveEngine === 'sqlite' || (typeof Capacitor !== 'undefined' && Capacitor.isNativePlatform?.())) {
            console.error('[visitaOfflineQueue] Error crítico en SQLite al eliminar entry; prohibido degradar a IndexedDB:', driverError);
            throw driverError;
        }
    }

    const db = await ensureOfflineStoreReady();
    if (!db) {
        const entries = readFallbackStore();
        const existing = entries.find((entry) => entry.key === key);
        if (existing) {
            if (!force && (!expectedRevisionId || existing.revisionId !== expectedRevisionId)) {
                return false;
            }
            removeFallbackEntry(key);
            emitQueueChanged();
            return true;
        }
        return false;
    }

    let wasDeleted = false;

    try {
        wasDeleted = await new Promise((resolve, reject) => {
            let markedForDeletion = false;

            const tx = db.transaction(STORE_NAME, "readwrite");
            const store = tx.objectStore(STORE_NAME);

            tx.oncomplete = () => {
                resolve(markedForDeletion);
            };

            tx.onerror = () => {
                reject(tx.error || new Error("Error en transacción IndexedDB"));
            };

            tx.onabort = () => {
                resolve(false);
            };

            const getRequest = store.get(key);

            getRequest.onsuccess = () => {
                const current = getRequest.result;

                if (!current) {
                    markedForDeletion = false;
                    return;
                }

                if (!force) {
                    if (!expectedRevisionId || !current.revisionId || current.revisionId !== expectedRevisionId) {
                        markedForDeletion = false;
                        return;
                    }
                }

                const deleteRequest = store.delete(key);
                deleteRequest.onsuccess = () => {
                    markedForDeletion = true;
                };
                deleteRequest.onerror = () => {
                    tx.abort();
                };
            };

            getRequest.onerror = () => {
                tx.abort();
            };
        });
    } catch {
        return false;
    }

    if (wasDeleted) {
        emitQueueChanged();
    }

    return wasDeleted;
}

export async function getOfflineEntriesByUser(userId) {
    const normalizedUserId = toNumber(userId);

    const driver = getOfflineStorageDriver();
    try {
        for (const emergency of readFallbackStore().filter((item) => item.userId === normalizedUserId)) {
            await withVisitaLocalLock(userId, emergency.visitaId, async () => {
                await recoverEmergencyEntry(driver, emergency.key, await driver.get(OFFLINE_STORES.ENTRIES, emergency.key));
            });
        }
        const allItems = await driver.getAll(OFFLINE_STORES.ENTRIES, null, { userId: normalizedUserId });
        if (Array.isArray(allItems)) {
            const filtered = allItems.filter((entry) => {
                if (!entry?.payload) {
                    return false;
                }
                if (typeof entry?.key === 'string' && entry.key.startsWith('conflict:')) {
                    return false;
                }
                if (normalizedUserId === null) {
                    return true;
                }
                return toNumber(entry?.userId) === normalizedUserId;
            });
            return sortEntriesByUpdatedAt(filtered);
        }
    } catch (driverError) {
        if (driver?.strict || driver?.effectiveEngine === 'sqlite' || (typeof Capacitor !== 'undefined' && Capacitor.isNativePlatform?.())) {
            console.error('[visitaOfflineQueue] Error crítico en SQLite al leer entries; prohibido degradar a IndexedDB:', driverError);
            throw driverError;
        }
    }

    const db = await ensureOfflineStoreReady();

    if (!db) {
        const entries = readFallbackStore().filter((entry) => {
            if (typeof entry?.key === 'string' && entry.key.startsWith('conflict:')) {
                return false;
            }
            if (normalizedUserId === null) {
                return true;
            }

            return toNumber(entry?.userId) === normalizedUserId;
        });

        return sortEntriesByUpdatedAt(entries);
    }

    const entries = await withStore(db, "readonly", (store, resolve, reject) => {
        const request = store.getAll();
        request.onsuccess = () => resolve(Array.isArray(request.result) ? request.result : []);
        request.onerror = () => reject(request.error);
    });

    return sortEntriesByUpdatedAt(
        entries.filter((entry) => {
            if (!entry?.payload) {
                return false;
            }
            if (typeof entry?.key === 'string' && entry.key.startsWith('conflict:')) {
                return false;
            }
            if (normalizedUserId === null) {
                return true;
            }

            return toNumber(entry?.userId) === normalizedUserId;
        }),
    );
}

export function subscribeOfflineQueueChanges(listener) {
    if (typeof window === "undefined" || typeof listener !== "function") {
        return () => {};
    }

    window.addEventListener(QUEUE_CHANGED_EVENT, listener);
    return () => window.removeEventListener(QUEUE_CHANGED_EVENT, listener);
}

export async function getOfflinePendingCountByUser(userId) {
    const entries = await getOfflineEntriesByUser(userId);
    return entries.length;
}

export async function getOfflineEntryByVisita(visitaId, userId = null) {
    if (!userId) {
        return null;
    }
    const entries = await getOfflineEntriesByUser(userId);
    const targetVisitaId = toNumber(visitaId);
    return entries.find((entry) => toNumber(entry.visitaId) === targetVisitaId) || null;
}

export function applyOfflinePayloadToVisita(visita, payload) {
    if (!visita || typeof visita !== "object") {
        return visita;
    }

    if (!payload || typeof payload !== "object") {
        return visita;
    }

    return {
        ...visita,
        ...Object.fromEntries(['instance_owners', 'excluded_from_report_fields', 'excluded_subgroup_instances',
            'excluded_manual', 'excluded_auto', 'hidden_response_fields'].filter((name) => payload[name] !== undefined)
            .map((name) => [name, payload[name]])),
        responses: payload.responses || visita.responses || {},
        dynamic_group_items:
            payload.dynamic_group_items || visita.dynamic_group_items || [],
        custom_subgroup_names:
            payload.custom_subgroup_names || visita.custom_subgroup_names || {},
        instance_states: payload.instance_states || visita.instance_states || {},
        observacion:
            payload.observacion !== undefined
                ? payload.observacion
                : visita.observacion || null,
        estado_sistema:
            payload.estado_sistema !== undefined
                ? payload.estado_sistema
                : visita.estado_sistema || null,
    };
}

/**
 * Extrae de forma defensiva los datos asociados a instancias descartadas por el servidor
 * para preservar el trabajo del técnico en caso de descarte o conflicto.
 *
 * @param {object} payload - Snapshot o payload de respuestas y estados
 * @param {Array<string>} discardedInstances - Lista de identificadores de instancia ('0_5', etc.)
 * @returns {object|null} Objeto con las respuestas, estados y nombres de las instancias afectadas
 */
/**
 * Extrae de forma defensiva los datos y fotos asociados a instancias descartadas por el servidor
 * para preservar el trabajo del técnico en caso de descarte o conflicto.
 *
 * @param {object} payload - Snapshot o payload de respuestas y estados
 * @param {Array<string>} discardedInstances - Lista de identificadores de instancia ('0_5', etc.)
 * @param {object} [options]
 * @param {Array<object>} [options.mediaList] - Lista de fotos para asociar por instanceRef
 * @param {number|string} [options.userId]
 * @param {string} [options.revisionId]
 * @returns {object|null} Objeto con las respuestas, estados, fotos y nombres de las instancias afectadas
 */
export function extractDiscardedConflictData(payload, discardedInstances = [], options = {}) {
    if (!payload || !Array.isArray(discardedInstances) || discardedInstances.length === 0) {
        return null;
    }

    const discardedSet = new Set(discardedInstances.map(String));
    const conflictResponses = {};
    const conflictInstanceStates = {};
    const conflictCustomSubgroupNames = {};
    let hasConflictData = false;

    if (payload.responses && typeof payload.responses === "object") {
        for (const [key, value] of Object.entries(payload.responses)) {
            const match = key.match(/^(\d+_\d+)(?:_.*)?$/);
            const instKey = match ? match[1] : null;
            if ((instKey && discardedSet.has(instKey)) || discardedSet.has(key)) {
                conflictResponses[key] = value;
                hasConflictData = true;
            }
        }
    }

    if (payload.instance_states && typeof payload.instance_states === "object") {
        for (const [key, value] of Object.entries(payload.instance_states)) {
            if (discardedSet.has(key)) {
                conflictInstanceStates[key] = value;
                hasConflictData = true;
            }
        }
    }

    if (payload.custom_subgroup_names && typeof payload.custom_subgroup_names === "object") {
        for (const [key, value] of Object.entries(payload.custom_subgroup_names)) {
            const match = key.match(/^(\d+_\d+)(?:_.*)?$/);
            const instKey = match ? match[1] : null;
            if ((instKey && discardedSet.has(instKey)) || discardedSet.has(key)) {
                conflictCustomSubgroupNames[key] = value;
                hasConflictData = true;
            }
        }
    }

    // Extraer y asociar fotos vinculadas a las instancias descartadas
    const conflictMedia = [];
    const mediaCandidates = Array.isArray(options.mediaList) ? options.mediaList : (Array.isArray(payload.media) ? payload.media : []);
    for (const m of mediaCandidates) {
        const instRef = String(m?.metadata?.instanceRef || m?.instance_ref || m?.instanceRef || "");
        if (instRef && discardedSet.has(instRef)) {
            conflictMedia.push({
                id: m.id || `media_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
                filename: m.filename || null,
                filePath: m.filePath || m.file_path || null,
                instanceRef: instRef,
                instanceLabel: m?.metadata?.instanceLabel || m?.instanceLabel || "",
                descripcion: m.descripcion || "",
                createdAt: m.createdAt || new Date().toISOString(),
            });
            hasConflictData = true;
        }
    }

    if (!hasConflictData) {
        return null;
    }

    const visitaId = payload.visitaId || options.visitaId || null;
    return {
        visitaId: visitaId ? Number(visitaId) : null,
        userId: options.userId ? Number(options.userId) : (payload.userId ? Number(payload.userId) : null),
        revisionId: options.revisionId || payload.revisionId || null,
        discardedInstances: Array.from(discardedSet),
        responses: conflictResponses,
        instance_states: conflictInstanceStates,
        custom_subgroup_names: conflictCustomSubgroupNames,
        media: conflictMedia,
        timestamp: new Date().toISOString(),
    };
}

/**
 * Guarda de forma duradera en el motor SQLite (y espejo localStorage) los datos de conflicto.
 */
const conflictOwner = value => Number.isSafeInteger(Number(value)) && Number(value) > 0 ? Number(value) : null;
const conflictRoot = (userId, visitaId) => `conflict:${userId}:${visitaId}`;
const conflictEntry = record => ({ key: record.key, userId: record.userId, visitaId: record.visitaId,
    revisionId: record.revisionId, payload: record, updatedAt: record.timestamp });
const ownsConflict = (record, userId, visitaId) => conflictOwner(record?.userId) === userId
    && conflictOwner(record?.visitaId) === visitaId;
const sameConflict = (left, right) => conflictSnapshotContent(left) === conflictSnapshotContent(right);

async function readOwnedConflicts(driver, userId, visitaId) {
    const root = conflictRoot(userId, visitaId);
    const entries = await driver.getAll(OFFLINE_STORES.ENTRIES,
        entry => entry.key === root || String(entry.key).startsWith(`${root}:`), { userId });
    return entries.map(entry => {
        if (!ownsConflict(entry, userId, visitaId) || !ownsConflict(entry.payload, userId, visitaId)
            || (entry.payload.key && entry.payload.key !== entry.key)) throw new Error('El conflicto local requiere revisar su propietario.');
        return { ...entry.payload, key: entry.key };
    });
}

function readConflictBackups(userId, visitaId) {
    if (typeof window === 'undefined' || !window.localStorage) return [];
    const local = window.localStorage, prefix = `visita_conflict_${userId}_${visitaId}`;
    const keys = new Set([prefix, `visita_conflict_${visitaId}`]);
    for (let index = 0; index < local.length; index++) {
        const key = local.key(index);
        if (key?.startsWith(`${prefix}:`)) keys.add(key);
    }
    const backups = [];
    for (const key of keys) {
        const raw = local.getItem(key);
        if (!raw) continue;
        let record;
        try { record = JSON.parse(raw); } catch { throw new Error('El respaldo local del conflicto requiere recuperación.'); }
        if (ownsConflict(record, userId, visitaId)) backups.push({ key, raw, record });
    }
    return backups;
}

function notifyConflict(visitaId, userId) {
    if (typeof window !== 'undefined' && typeof CustomEvent !== 'undefined') {
        window.dispatchEvent(new CustomEvent('visita-conflict-updated', { detail: { visitaId, userId } }));
    }
}

export async function saveVisitaConflictData(visitaId, conflictData, options = {}) {
    if (!visitaId || !conflictData) return null;
    const owner = conflictOwner(conflictData.userId ?? options.userId), visit = conflictOwner(visitaId);
    if (!owner || !visit || (options.userId != null && conflictOwner(options.userId) !== owner)) {
        throw new Error('El conflicto requiere un propietario explícito.');
    }
    return withVisitaLocalLock(owner, visit, async () => {
        const uuid = generateRevisionUuid(), key = `${conflictRoot(owner, visit)}:${uuid}`;
        const record = { ...conflictData, id: key, key, conflictUuid: uuid, userId: owner, visitaId: visit,
            revisionId: String(conflictData.revisionId || options.revisionId || uuid),
            timestamp: conflictData.timestamp || new Date().toISOString(), discardedInstances: conflictData.discardedInstances || [],
            responses: conflictData.responses || {}, instance_states: conflictData.instance_states || {},
            custom_subgroup_names: conflictData.custom_subgroup_names || {}, media: conflictData.media || [],
            status: 'pending_resolution', resolution: null };
        let persistenceError;
        try {
            const driver = getOfflineStorageDriver();
            const previous = (await readOwnedConflicts(driver, owner, visit)).find(item => sameConflict(item, record));
            if (previous) return previous; // Una repetición tampoco resucita una resolución anterior.
            await driver.put(OFFLINE_STORES.ENTRIES, conflictEntry(record));
            const verified = await driver.get(OFFLINE_STORES.ENTRIES, key);
            if (JSON.stringify(verified?.payload) !== JSON.stringify(record)) throw new Error('No se pudo verificar el conflicto guardado.');
        } catch (error) { persistenceError = error; }
        try {
            window.localStorage.setItem(`visita_conflict_${owner}_${visit}:${uuid}`, JSON.stringify(record));
        } catch { /* El espejo nunca sustituye la confirmación de SQLite. */ }
        notifyConflict(visit, owner);
        if (persistenceError) throw persistenceError;
        return record;
    });
}

/**
 * Recupera el registro de conflicto desde el motor SQLite offline o localStorage.
 */
export async function getVisitaConflictData(visitaId, userId = null) {
    const owner = conflictOwner(userId), visit = conflictOwner(visitaId);
    if (!owner || !visit) return null;
    const durable = await readOwnedConflicts(getOfflineStorageDriver(), owner, visit);
    const pending = durable.filter(record => record.status === 'pending_resolution');
    for (const { record } of readConflictBackups(owner, visit)) {
        if (record.status === 'pending_resolution' && !durable.some(item => sameConflict(item, record))) {
            pending.push(record);
        }
    }
    return pending.sort((a, b) => String(a.timestamp || '').localeCompare(String(b.timestamp || ''))
        || String(a.key || '').localeCompare(String(b.key || '')))[0] || null;
}

/**
 * Resuelve formalmente un conflicto parcial (descarte definitivo o reincorporación como nuevo ítem).
 */
export async function resolveVisitaConflict(visitaId, { action = 'discard', userId = null, newInstanceKey = null, expectedConflictKey = null } = {}) {
    const owner = conflictOwner(userId), visit = conflictOwner(visitaId);
    if (!owner || !visit) throw new Error('La resolución requiere un propietario explícito.');
    if (!['discard', 'reapply_as_new'].includes(action)) throw new Error('Acción de resolución inválida.');
    return withVisitaLocalLock(owner, visit, async () => {
        const driver = getOfflineStorageDriver(), root = conflictRoot(owner, visit);
        const existing = await getVisitaConflictData(visit, owner);
        if (!existing || (expectedConflictKey && existing.key !== expectedConflictKey)) {
            throw new Error('El conflicto cambió. Revisá la versión pendiente antes de resolverlo.');
        }
        const key = existing.key || `${root}:${generateRevisionUuid()}`;
        if (key !== root && !key.startsWith(`${root}:`)) throw new Error('La clave del conflicto requiere recuperación.');
        const resolvedRecord = { ...existing, id: key, key,
            status: action === 'reapply_as_new' ? 'resolved_reapplied' : 'resolved_discarded',
            resolution: { action, resolvedAt: new Date().toISOString(), resolvedBy: owner, newInstanceKey } };
        const mutation = await driver.mutate(OFFLINE_STORES.ENTRIES, key, current => {
            if (current && (!ownsConflict(current, owner, visit) || current.payload?.status !== 'pending_resolution'
                || !sameConflict(current.payload, existing))) return null;
            // Si solo existe el respaldo, comprobar que sigue siendo exactamente el observado.
            if (!current && !readConflictBackups(owner, visit).some(item => JSON.stringify(item.record) === JSON.stringify(existing))) return null;
            return conflictEntry(resolvedRecord);
        });
        const verified = await driver.get(OFFLINE_STORES.ENTRIES, key);
        if (!mutation || JSON.stringify(verified?.payload) !== JSON.stringify(resolvedRecord)
            || !ownsConflict(verified, owner, visit)) {
            throw new Error('No se pudo verificar la resolución guardada. Se conserva el respaldo del conflicto.');
        }
        await clearVisitaConflictData(visit, owner);
        notifyConflict(visit, owner);
        return resolvedRecord;
    });
}

/**
 * Limpia la alerta activa sin purgar el historial persistido en SQLite.
 */
export async function clearVisitaConflictData(visitaId, userId) {
    const owner = conflictOwner(userId), visit = conflictOwner(visitaId);
    if (!owner || !visit) return;
    const durable = await readOwnedConflicts(getOfflineStorageDriver(), owner, visit);
    for (const backup of readConflictBackups(owner, visit)) {
        if (durable.some(record => isResolvedConflict(record) && record.resolution?.resolvedBy === owner && sameConflict(record, backup.record))) {
            // Un espejo nuevo que llegó durante la lectura también se conserva.
            if (window.localStorage.getItem(backup.key) === backup.raw) window.localStorage.removeItem(backup.key);
        }
    }
}


